import { useMemo, useState } from 'react';
import { debounce } from 'lodash-es';

import { Box, Button, Flex, Heading, SimpleGrid } from '@chakra-ui/react';

import { CardCarousel, CardCarouselSlide } from '~/components/carousel/card-carousel';
import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { trackEvent } from '~/features/analytics/track-event';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { CatalogCard } from '~/features/experiments/ab_plp_redesign_2025/components/catalog-card';
import { EyeIcon, StarEmptyIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

const MOBILE_INITIAL_CARDS = 4;

export type GovernmentTopProgramsProps = {
  catalogItems: CatalogItem[];
  pretitle?: string;
  title?: string;
};

export function GovernmentTopPrograms({
  catalogItems,
  pretitle = 'Top-rated programs',
  title = 'Top-demanded tech skills and programs',
}: GovernmentTopProgramsProps) {
  // Memoize so the debounce timer persists across renders; recreating it each
  // render would reset the timer and let `top_programs_scrolled` fire far more
  // often than the intended 100ms throttle.
  const handleSlideMove = useMemo(
    () =>
      debounce(() => {
        trackEvent({
          type: 'click',
          name: 'Government CTA Clicked',
          label: 'top_programs_scrolled',
        });
      }, 100),
    []
  );

  function handleCardClick(catalogItem: CatalogItem) {
    trackEvent({
      type: 'click',
      name: 'Government CTA Clicked',
      label: 'top_programs_card_clicked',
      value: catalogItem.title ?? '',
      additionalAttributes: {
        product_ids: [catalogItem.key],
      },
    });
  }

  const slides: CardCarouselSlide[] = catalogItems.map((catalogItem, index) => ({
    key: catalogItem.key + index,
    content: <CatalogCard catalogItem={catalogItem} width='100%' onClick={() => handleCardClick(catalogItem)} />,
  }));

  return (
    <Flex as='section' bg='grey.50' flexDirection='column' py={{ base: '56px', md: '104px' }}>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <SectionHeader
          color='grey.500'
          containerProps={{ margin: '0 auto' }}
          iconSlot={<StarEmptyIcon color='grey.500' height='18px' width='18px' />}
          subtitle={pretitle}
          titleSlot={
            <Heading
              as='h2'
              color='grey.900'
              fontWeight='600'
              lineHeight='1.1'
              fontSize={{
                base: '2.5rem',
                md: '3.375rem',
              }}
            >
              {title}
            </Heading>
          }
        />
      </ContentContainer>

      <Box mt={{ base: '40px', md: '48px' }}>
        <Box display={{ base: 'none', xl: 'block' }}>
          <ContentContainer contentMaxWidth='content-max-width-md'>
            <CardCarousel
              slides={slides}
              onSlideMove={handleSlideMove}
              onSlideNext={handleSlideMove}
              onSlidePrev={handleSlideMove}
            />
          </ContentContainer>
        </Box>

        <Box display={{ base: 'block', xl: 'none' }}>
          <ContentContainer contentMaxWidth='content-max-width-md'>
            <ProgramGrid catalogItems={catalogItems} onCardClick={handleCardClick} />
          </ContentContainer>
        </Box>
      </Box>
    </Flex>
  );
}

function ProgramGrid({
  catalogItems,
  onCardClick,
}: {
  catalogItems: CatalogItem[];
  onCardClick: (catalogItem: CatalogItem) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const visibleItems = showAll ? catalogItems : catalogItems.slice(0, MOBILE_INITIAL_CARDS);

  function handleShowAllClick() {
    trackEvent({
      type: 'click',
      name: 'Government CTA Clicked',
      label: showAll ? 'top_programs_show_less_clicked' : 'top_programs_show_all_clicked',
    });
    setShowAll((prev) => !prev);
  }

  return (
    <Flex flexDirection='column' gap='24px'>
      <SimpleGrid columns={2} gap='16px'>
        {visibleItems.map((catalogItem) => (
          <CatalogCard
            key={catalogItem.key}
            catalogItem={catalogItem}
            width='100%'
            onClick={() => onCardClick(catalogItem)}
          />
        ))}
      </SimpleGrid>

      {catalogItems.length > MOBILE_INITIAL_CARDS && (
        <Button
          alignSelf='center'
          borderColor='blue.700'
          color='blue.700'
          fontWeight='600'
          gap='8px'
          variant='outline'
          width={{ base: '100%', md: 'fit-content' }}
          onClick={handleShowAllClick}
        >
          <EyeIcon h='18px' w='18px' />
          {showAll ? 'Show less programs' : 'Show all programs'}
        </Button>
      )}
    </Flex>
  );
}
